import userflow, { Attributes, EventAttributes } from 'userflow.js';

import {
	AnalyticsPlugin,
	AnalyticsProperties,
	Properties,
	UserAnalyticsProperties,
	WorkspaceAnalyticsProperties,
} from '../analytics';

interface UserflowsConfig {
	userflowToken: string;
}

interface QueuedTrack {
	event: string;
	props?: Properties;
}

const MAX_INIT_ATTEMPTS = 4;

export class Userflows implements AnalyticsPlugin {
	private _initAttempts = 0;
	private hasInitialized = false;
	private hasIdentified = false;
	private isAvailable = true;
	private pendingIdentify: AnalyticsProperties | null = null;
	private trackQueue: QueuedTrack[] = [];

	constructor(private readonly config: UserflowsConfig) {}

	get name(): string {
		return 'userflows';
	}

	private getCompanyAttributes(props: WorkspaceAnalyticsProperties): Attributes {
		return {
			name: props.workspaceName,
			isInBetaProgram: props.isInBetaProgram,
			availableFeatures: props.availableFeatures,
			enabledFeatures: props.enabledFeatures,
			featureFlags: props.featureFlags,
			conversionDate: props.conversionDate,
			creationDate: props.creationDate,
			renewalDate: props.renewalDate,
			registrationDate: props.registrationDate,
			industry: props.industry,
			tags: props.tags,
			organisationId: props.organisationId,
			itemQuota: props.itemQuota,
			itemQuotaPercentage: props.itemQuotaPercentage,
			subscriptionPlan: props.subscriptionPlan,
			billingPeriod: props.billingPeriod,
			progress: props.progress,
		};
	}

	private getUserAttributes(props: UserAnalyticsProperties): Attributes {
		return {
			userId: props.userId,
			name: props.name,
			email: props.email,
			screenWidth: props.screenWidth,
			screenHeight: props.screenHeight,
		};
	}

	private getUserOptionsForGroup(props: UserAnalyticsProperties): Attributes {
		return {
			seatType: props.seatType,
			role: props.userRole,
			userTimezone: props.userTimezone,
			isAccountOwner: props.isAccountOwner,
			isAdministrator: props.isAdministrator,
			isSyncedUser: props.isSyncedUser,
			canLoginWithSSO: props.canLoginWithSSO,
		};
	}

	private flushTrackQueue(): void {
		const queue = this.trackQueue.splice(0);
		for (const { event, props } of queue) {
			try {
				void userflow?.track(event, props ? this.transformPageAttributes(props) : undefined)?.catch((error) => {
					// eslint-disable-next-line no-console
					console.error('Error tracking event', error);
				});
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error tracking event', error);
			}
		}
	}

	private disableAfterLoadFailure(error: unknown): void {
		this.isAvailable = false;
		this.hasInitialized = false;
		this.hasIdentified = false;
		this.pendingIdentify = null;
		this.trackQueue = [];
		// eslint-disable-next-line no-console
		console.warn('Userflow.js could not be loaded; userflow analytics disabled.', error);
	}

	private runIdentify(props: AnalyticsProperties): void {
		if (!this.isAvailable || !userflow || !props.userId) return;
		void userflow
			?.identify(props.userId, this.getUserAttributes(props))
			?.then(() => {
				this.hasIdentified = true;
				void userflow?.group(props.workspaceId, this.getCompanyAttributes(props), {
					membership: this.getUserOptionsForGroup(props),
				});
				this.flushTrackQueue();
			})
			?.catch((error) => {
				// eslint-disable-next-line no-console
				console.error('Error identifying user', error);
			});
	}

	private flushPendingIdentify(): void {
		if (!this.pendingIdentify) return;
		const props = this.pendingIdentify;
		this.pendingIdentify = null;
		this.runIdentify(props);
	}

	identify(props: AnalyticsProperties): void {
		if (!this.isAvailable || !props.userId) return;

		if (!this.hasInitialized) {
			this.pendingIdentify = props;
			return;
		}

		this.runIdentify(props);
	}

	initialize(): void {
		// Skip userflow under Playwright. The window marker is set by
		if (typeof window !== 'undefined' && window.__PLAYWRIGHT_TEST__) {
			this.isAvailable = false;
			return;
		}

		if (this.hasInitialized) return;

		if (!userflow?.init) {
			if (this._initAttempts >= MAX_INIT_ATTEMPTS) {
				this.disableAfterLoadFailure(
					new Error('Userflow.js could not be loaded; userflow analytics disabled.')
				);
				return;
			}
			this._initAttempts++;
			setTimeout(() => {
				this.initialize();
			}, 200 * this._initAttempts);
			return;
		}
		try {
			userflow.init(this.config.userflowToken);
			this.hasInitialized = true;
			this.flushPendingIdentify();
		} catch (error) {
			this.disableAfterLoadFailure(error);
		}
	}

	page(): void {
		// userflow?.page();
	}

	transformPageAttributes(props: Properties): EventAttributes {
		return Object.entries(props).reduce((previous, [key, value]) => {
			previous[key] = value as EventAttributes[string];
			return previous;
		}, {} as EventAttributes);
	}

	track(event: string, source?: string, props?: Properties): void {
		if (!this.isAvailable) return;

		const baseProps = props ?? {};
		const propsWithSource = source ? { ...baseProps, source } : baseProps;
		if (!this.hasIdentified || !this.hasInitialized) {
			this.trackQueue.push({ event, props: propsWithSource });
			return;
		}
		void userflow?.track(event, this.transformPageAttributes(propsWithSource))?.catch((error) => {
			// eslint-disable-next-line no-console
			console.error('Error tracking event', error);
		});
	}
}
